import { useState } from 'react';
import { Link } from 'react-router-dom';

const PORTFOLIO_ITEMS = [
  {
    title: 'Kitchen Deep Clean',
    location: 'Port St. Lucie, FL',
    category: 'Residential',
    img: 'https://public.readdy.ai/ai/img_res/819d687f7475709a01a90554bb580da7.jpg',
  },
  {
    title: 'Luxury Bathroom Restoration',
    location: 'Stuart, FL',
    category: 'Residential',
    img: 'https://readdy.ai/api/search-image?query=luxury%20bathroom%20after%20professional%20cleaning%20sparkling%20white%20tiles%20shining%20chrome%20faucets%20pristine%20mirrors%20fresh%20white%20towels%20elegant%20tropical%20south%20florida%20home%20interior%20gleaming%20surfaces&width=700&height=500&seq=pf2&orientation=landscape',
  },
  {
    title: 'Corporate Office Suite',
    location: 'Jensen Beach, FL',
    category: 'Commercial',
    img: 'https://readdy.ai/api/search-image?query=modern%20clean%20corporate%20office%20after%20commercial%20cleaning%20polished%20floors%20organized%20workstations%20bright%20windows%20plants%20neat%20professional%20environment%20south%20florida%20business&width=700&height=500&seq=pf3&orientation=landscape',
  },
  {
    title: 'Move-Out Whole Home',
    location: 'Palm City, FL',
    category: 'Residential',
    img: 'https://readdy.ai/api/search-image?query=spotless%20empty%20home%20after%20move-out%20cleaning%20pristine%20hardwood%20floors%20clean%20walls%20bright%20sunlit%20rooms%20fresh%20and%20empty%20south%20florida%20house%20interior%20clean%20ready%20for%20new%20owners&width=700&height=500&seq=pf4&orientation=landscape',
  },
  {
    title: 'Retail Showroom',
    location: 'Fort Pierce, FL',
    category: 'Commercial',
    img: 'https://readdy.ai/api/search-image?query=gleaming%20retail%20store%20showroom%20after%20commercial%20cleaning%20polished%20floors%20spotless%20display%20cases%20clean%20windows%20bright%20lighting%20elegant%20merchandise%20display%20south%20florida&width=700&height=500&seq=pf5&orientation=landscape',
  },
  {
    title: 'Master Bedroom Refresh',
    location: 'Hobe Sound, FL',
    category: 'Residential',
    img: 'https://readdy.ai/api/search-image?query=beautiful%20master%20bedroom%20after%20professional%20cleaning%20freshly%20made%20luxury%20bed%20clean%20white%20linens%20dusted%20furniture%20polished%20floors%20neat%20organized%20tropical%20south%20florida%20bedroom%20interior&width=700&height=500&seq=pf6&orientation=landscape',
  },
  {
    title: 'Post-Construction Cleanup',
    location: 'Tradition, FL',
    category: 'Specialty',
    img: 'https://readdy.ai/api/search-image?query=newly%20renovated%20home%20after%20post-construction%20cleaning%20fresh%20paint%20walls%20clean%20tile%20floors%20bright%20open%20space%20modern%20south%20florida%20new%20construction%20interior%20clean%20dust-free%20pristine&width=700&height=500&seq=pf7&orientation=landscape',
  },
  {
    title: 'Medical Office Sanitization',
    location: 'Port St. Lucie, FL',
    category: 'Commercial',
    img: 'https://readdy.ai/api/search-image?query=spotless%20medical%20office%20reception%20and%20examination%20room%20after%20professional%20deep%20sanitization%20cleaning%20clean%20white%20surfaces%20organized%20equipment%20bright%20sterile%20environment%20south%20florida%20clinic&width=700&height=500&seq=pf8&orientation=landscape',
  },
  {
    title: 'Vacation Rental Turnover',
    location: 'Jensen Beach, FL',
    category: 'Specialty',
    img: 'https://readdy.ai/api/search-image?query=beautiful%20vacation%20rental%20home%20after%20professional%20turnover%20cleaning%20fresh%20bed%20linens%20spotless%20kitchen%20sparkling%20bathroom%20elegant%20tropical%20south%20florida%20beach%20house%20interior%20ready%20for%20guests&width=700&height=500&seq=pf9&orientation=landscape',
  },
];

const CATEGORIES = ['All', 'Residential', 'Commercial', 'Specialty'];

const SITE_URL = import.meta.env.VITE_SITE_URL || '';

export default function PortfolioPage() {
  const [activeCategory, setActiveCategory] = useState('All');
  const [lightbox, setLightbox] = useState<null | typeof PORTFOLIO_ITEMS[0]>(null);

  const filtered = activeCategory === 'All'
    ? PORTFOLIO_ITEMS
    : PORTFOLIO_ITEMS.filter((p) => p.category === activeCategory);

  const portfolioSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: 'A&S Prestige Cleaning Service Portfolio',
    url: `${SITE_URL}/portfolio`,
    description: 'Before and after photos of professional cleaning projects in Port St. Lucie and Martin County, FL — residential, commercial, and specialty cleaning.',
    provider: {
      '@type': 'CleaningService',
      name: 'A&S Prestige Cleaning Service',
      url: SITE_URL,
    },
    hasPart: PORTFOLIO_ITEMS.map((item) => ({
      '@type': 'ImageObject',
      name: item.title,
      contentUrl: item.img,
      description: `${item.title} by A&S Cleaning Service in ${item.location}`,
      author: {
        '@type': 'Organization',
        name: 'A&S Prestige Cleaning Service',
      },
    })),
  };

  return (
    <main>
      <title>Cleaning Portfolio | Before &amp; After Results | A&S Cleaning Service FL</title>
      <meta name="description" content="Browse A&S Cleaning Service's portfolio of completed residential and commercial cleaning projects across Port St. Lucie, Stuart, Jensen Beach and Martin County, FL." />
      <meta name="keywords" content="cleaning portfolio port st lucie, before after cleaning results florida, residential cleaning results martin county, commercial cleaning photos stuart fl" />
      <link rel="canonical" href={`${SITE_URL}/portfolio`} />
      <meta property="og:title" content="Cleaning Portfolio | Before &amp; After | A&S Cleaning Service" />
      <meta property="og:description" content="Real before and after cleaning results from homes and businesses across Port St. Lucie and Martin County, FL." />
      <meta property="og:url" content={`${SITE_URL}/portfolio`} />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(portfolioSchema) }}
      />

      {/* Hero */}
      <section className="relative pt-32 pb-20">
        <img
          src="https://readdy.ai/api/search-image?query=beautiful%20portfolio%20gallery%20of%20cleaned%20homes%20and%20commercial%20spaces%20collage%20bright%20vibrant%20professional%20cleaning%20results%20south%20florida%20interior%20design&width=1440&height=500&seq=pfhero&orientation=landscape"
          alt="Portfolio of cleaning projects"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/60" />
        <div className="relative z-10 text-center px-6 max-w-3xl mx-auto">
          <span className="inline-block bg-emerald-600/90 text-white text-xs font-bold tracking-widest uppercase px-5 py-2 rounded-full mb-5">
            Our Work
          </span>
          <h1 className="text-5xl lg:text-6xl font-extrabold text-white mb-5 leading-tight" style={{ fontFamily: "'Montserrat', sans-serif" }}>
            Our Portfolio
          </h1>
          <p className="text-gray-200 text-lg max-w-xl mx-auto">
            Real results from real clients across Port St. Lucie and Martin County. Browse our completed cleaning projects.
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex justify-center mb-10 gap-3 flex-wrap">
            {CATEGORIES.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-6 py-2.5 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                  activeCategory === cat
                    ? 'bg-emerald-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((item) => (
              <div
                key={item.title}
                className="rounded-2xl overflow-hidden group cursor-pointer"
                onClick={() => setLightbox(item)}
              >
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={item.img}
                    alt={item.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-5">
                    <div className="w-10 h-10 flex items-center justify-center bg-white rounded-full ml-auto">
                      <i className="ri-zoom-in-line text-gray-900" />
                    </div>
                  </div>
                  <span className="absolute top-4 left-4 bg-emerald-600 text-white text-xs font-bold px-3 py-1 rounded-full">
                    {item.category}
                  </span>
                </div>
                <div className="bg-gray-50 px-5 py-4">
                  <h3 className="font-bold text-gray-900 text-base">{item.title}</h3>
                  <div className="flex items-center gap-1 mt-1">
                    <i className="ri-map-pin-line text-emerald-600 text-xs" />
                    <span className="text-gray-400 text-xs">{item.location}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-emerald-600 py-16">
        <div className="max-w-3xl mx-auto text-center px-6">
          <h2 className="text-3xl font-extrabold text-white mb-4" style={{ fontFamily: "'Montserrat', sans-serif" }}>
            Ready to Transform Your Space?
          </h2>
          <p className="text-emerald-100 mb-8">Join hundreds of satisfied clients across Port St. Lucie and Martin County.</p>
          <Link to="/contact" className="bg-white text-emerald-700 hover:bg-emerald-50 font-bold px-10 py-4 rounded-full transition-colors whitespace-nowrap cursor-pointer inline-block">
            Book Your Cleaning Today
          </Link>
        </div>
      </section>

      {/* Lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 bg-black/80 z-[100] flex items-center justify-center p-6"
          onClick={() => setLightbox(null)}
        >
          <div
            className="bg-white rounded-2xl overflow-hidden max-w-2xl w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <img src={lightbox.img} alt={lightbox.title} className="w-full h-72 object-cover object-top" />
            <div className="p-6 flex items-start justify-between">
              <div>
                <h3 className="text-xl font-bold text-gray-900">{lightbox.title}</h3>
                <div className="flex items-center gap-2 mt-1">
                  <span className="bg-emerald-100 text-emerald-700 text-xs font-bold px-3 py-1 rounded-full">{lightbox.category}</span>
                  <div className="flex items-center gap-1">
                    <i className="ri-map-pin-line text-emerald-600 text-xs" />
                    <span className="text-gray-400 text-sm">{lightbox.location}</span>
                  </div>
                </div>
              </div>
              <button
                className="w-9 h-9 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-full cursor-pointer transition-colors"
                onClick={() => setLightbox(null)}
              >
                <i className="ri-close-line text-gray-700" />
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
